import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Share2, ShieldCheck, Radio, Zap, Eye, DollarSign, Clock, Lightbulb, Globe } from 'lucide-react';
import { useOs2, PageHead, Card, timeAgo, Fade, LoadingState } from './os2ui';
import { authFetch } from '../../lib/auth';

/* Social Intelligence Center — ONE unified view above all providers.
   Every status is evidence-based and explicitly labeled; never a generic "Connected". */

const STATUS_META = {
  operational: { es: 'Operativo', en: 'Operational', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  degraded: { es: 'Degradado', en: 'Degraded', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  attention_required: { es: 'Requiere atención', en: 'Attention required', cls: 'bg-red-50 text-red-600 border-red-200', dot: 'bg-red-500' },
  authentication_required: { es: 'Reautenticar', en: 'Re-authenticate', cls: 'bg-red-50 text-red-600 border-red-200', dot: 'bg-red-500' },
  paused: { es: 'Pausado', en: 'Paused', cls: 'bg-gray-50 text-gray-500 border-gray-200', dot: 'bg-gray-400' },
  manual: { es: 'Manual', en: 'Manual', cls: 'bg-sky-50 text-sky-600 border-sky-200', dot: 'bg-sky-400' },
  unknown: { es: 'Sin verificar', en: 'Unverified', cls: 'bg-gray-50 text-gray-500 border-gray-200', dot: 'bg-gray-400' },
};

const EVIDENCE_META = {
  api_confirmed: { es: 'Confirmado por API', en: 'API confirmed', cls: 'bg-emerald-100 text-emerald-800' },
  internal_publication_confirmed: { es: 'Envío interno confirmado', en: 'Internal send confirmed', cls: 'bg-emerald-100 text-emerald-800' },
  agent_confirmed: { es: 'Confirmado por agente', en: 'Agent confirmed', cls: 'bg-sky-100 text-sky-700' },
  analytics_confirmed: { es: 'Confirmado por analytics', en: 'Analytics confirmed', cls: 'bg-sky-100 text-sky-700' },
  rss_activity_confirmed: { es: 'Actividad RSS (proveedor)', en: 'RSS activity (provider)', cls: 'bg-amber-100 text-amber-700' },
  manually_configured: { es: 'Configuración manual', en: 'Manually configured', cls: 'bg-gray-100 text-gray-500' },
  unverified: { es: 'Sin verificar', en: 'Unverified', cls: 'bg-gray-100 text-gray-500' },
};

const CHANNEL_META = {
  linkedin: { name: 'LinkedIn', icon: '/brands/linkedin.svg', tes: 'Red social', ten: 'Social Media' },
  facebook: { name: 'Facebook', icon: '/brands/facebook.svg', tes: 'Red social', ten: 'Social Media' },
  instagram: { name: 'Instagram', icon: '/brands/instagram.svg', tes: 'Red social', ten: 'Social Media' },
  x: { name: 'X', icon: '/brands/x.svg', tes: 'Red social', ten: 'Social Media' },
  threads: { name: 'Threads', icon: '/brands/threads.svg', tes: 'Red social', ten: 'Social Media' },
  blogger: { name: 'Blogger', icon: '/brands/blogger.svg', tes: 'Blog', ten: 'Blog' },
  newsletter: { name: 'Newsletter', icon: '/brands/newsletter.svg', tes: 'Email marketing', ten: 'Email Marketing' },
  youtube: { name: 'YouTube', icon: '/brands/youtube.svg', tes: 'Vídeo', ten: 'Video' },
  tiktok: { name: 'TikTok', icon: '/brands/tiktok.svg', tes: 'Vídeo corto', ten: 'Short Video' },
  reddit: { name: 'Reddit', icon: '/brands/reddit.svg', tes: 'Comunidad', ten: 'Community' },
  pinterest: { name: 'Pinterest', icon: '/brands/pinterest.svg', tes: 'Descubrimiento', ten: 'Discovery' },
  gbp: { name: 'Google Business', icon: '/brands/google.svg', tes: 'Búsqueda local', ten: 'Local Search' },
  google_business: { name: 'Google Business', icon: '/brands/google.svg', tes: 'Búsqueda local', ten: 'Local Search' },
};
const PROVIDER_CLS = {
  'dlvr.it': 'bg-sky-50 text-sky-700 border-sky-200',
  dlvrit: 'bg-sky-50 text-sky-700 border-sky-200',
  brevo: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  marblism: 'bg-violet-50 text-violet-700 border-violet-200',
  google: 'bg-blue-50 text-blue-700 border-blue-200',
  native: 'bg-gray-50 text-gray-600 border-gray-200',
};
const AGENT_AVATARS = ['rachel', 'penny', 'sonny', 'stan'];

function Kpi({ label, value, testid, accent }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-400">{label}</p>
      <p className={`mt-1 text-[24px] font-black tabular-nums leading-none ${accent || 'text-gray-900'}`}>{value}</p>
    </Card>
  );
}

/* NOC card: recognition first — brand logo, health, provider, agent. Reading is the fallback. */
function ChannelCard({ c, isEs }) {
  const st = STATUS_META[c.status] || STATUS_META.unknown;
  const ev = EVIDENCE_META[c.evidence] || EVIDENCE_META.unverified;
  const meta = CHANNEL_META[c.channel] || {};
  const agentKey = (String(c.responsible_agent || '').toLowerCase().match(/[a-z]+/) || [''])[0];
  const trend = typeof c.traffic_trend_pct === 'number' ? c.traffic_trend_pct : null;
  const provCls = PROVIDER_CLS[String(c.provider || '').toLowerCase()] || PROVIDER_CLS.native;
  return (
    <Card className="p-4" data-testid={`social-channel-${c.channel}`}>
      <div className="flex items-start gap-3">
        <span className="w-11 h-11 rounded-xl border border-gray-100 bg-white shadow-sm flex items-center justify-center shrink-0">
          {meta.icon
            ? <img src={meta.icon} alt="" className="w-8 h-8 object-contain" data-testid={`social-brand-icon-${c.channel}`} />
            : <Globe className="w-7 h-7 text-gray-300" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-black text-gray-900 leading-tight">{meta.name || c.channel}</p>
          <p className="mt-0.5 text-[9.5px] uppercase tracking-wider font-bold text-gray-400">{isEs ? meta.tes : meta.ten}</p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-bold ${st.cls}`} data-testid={`social-status-${c.channel}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${st.dot} ${c.status === 'operational' ? 'animate-pulse' : ''}`} /> {isEs ? st.es : st.en}
        </span>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <span className={`rounded-full border px-2 py-0.5 text-[9.5px] font-bold ${provCls}`} data-testid={`social-provider-${c.channel}`}>{String(c.provider).toLowerCase() === 'dlvrit' ? 'dlvr.it' : c.provider}</span>
        <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 pl-0.5 pr-2 py-0.5 text-[9.5px] font-bold text-gray-600 capitalize" data-testid={`social-agent-${c.channel}`}>
          {AGENT_AVATARS.includes(agentKey)
            ? <img src={`/avatars/${agentKey}.png`} alt={agentKey} className="w-4 h-4 rounded-full object-cover" />
            : <span className="w-4 h-4 rounded-full bg-gray-200 inline-block" />}
          {agentKey || c.responsible_agent}
        </span>
        <span className={`rounded px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide ${ev.cls}`} data-testid={`social-evidence-${c.channel}`}>
          {isEs ? ev.es : ev.en}
        </span>
        <span className="text-[9.5px] text-gray-400 uppercase">{isEs ? 'confianza' : 'confidence'}: {c.confidence}</span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 rounded-xl border border-gray-100 bg-gray-50/60 p-2.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <Eye className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-[13.5px] font-black tabular-nums text-gray-900 leading-none">
              {c.traffic_28d}
              {trend !== null && (
                <span className={`ml-1 text-[10px] font-bold ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{trend >= 0 ? '↗' : '↘'} {trend >= 0 ? '+' : ''}{trend}%</span>
              )}
            </p>
            <p className="text-[8.5px] text-gray-400 uppercase font-bold mt-0.5">{isEs ? 'visitas 28d' : 'visits 28d'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <DollarSign className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-[13.5px] font-black tabular-nums text-gray-900 leading-none">{c.conversions_28d}</p>
            <p className="text-[8.5px] text-gray-400 uppercase font-bold mt-0.5">{isEs ? 'conversiones' : 'conversions'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-[11.5px] font-bold text-gray-800 leading-none truncate">{c.last_confirmed_publication ? timeAgo(c.last_confirmed_publication, isEs) : '—'}</p>
            <p className="text-[8.5px] text-gray-400 uppercase font-bold mt-0.5">{isEs ? 'últ. publicación' : 'last post'}</p>
          </div>
        </div>
      </div>

      <p className="mt-2 text-[11px] text-gray-500 leading-snug">{c.status_reason}</p>
      {(c.evidences || []).slice(0, 2).map((e, i) => (
        <p key={i} className="mt-0.5 text-[10px] text-gray-400 truncate">· {(EVIDENCE_META[e.kind] || {})[isEs ? 'es' : 'en'] || e.kind}: {e.detail}</p>
      ))}
      <p className="mt-2 flex items-start gap-1 text-[10.5px] text-[#d8651a] font-semibold">
        <Lightbulb className="w-3 h-3 mt-0.5 shrink-0" /> {isEs ? c.recommended_action?.es : c.recommended_action?.en}
      </p>
      <p className="mt-1 text-[9.5px] text-gray-300">{isEs ? 'Verificado' : 'Verified'} {timeAgo(c.last_verified_at, isEs)}</p>
    </Card>
  );
}

const EV_STATUS = {
  ok: { es: 'confirmada', en: 'confirmed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  partial: { es: 'parcial', en: 'partial', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  none: { es: 'sin señal', en: 'no signal', cls: 'bg-gray-50 text-gray-400 border-gray-200' },
  unavailable: { es: 'no disponible aquí', en: 'unavailable here', cls: 'bg-gray-50 text-gray-300 border-gray-100' },
};

function EvidencePanel({ isEs }) {
  const [ev, setEv] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    authFetch('/admin/social/evidence').then((r) => (r.ok ? r.json() : Promise.reject(r.status))).then(setEv).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const confirmSighting = async () => {
    setSaving(true);
    try {
      await authFetch('/admin/social/evidence/sighting', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ network: 'linkedin', note: 'Post visto en la página oficial de LinkedIn' }) });
      load();
    } finally { setSaving(false); }
  };
  if (!ev) return null;
  const tone = ev.score >= 70 ? '#10b981' : ev.score >= 40 ? '#F77F11' : '#ef4444';
  const circ = 2 * Math.PI * 26;
  return (
    <Card className="mt-4 p-4" data-testid="social-evidence-panel">
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative w-16 h-16 shrink-0" data-testid="social-evidence-score">
          <svg viewBox="0 0 64 64" className="w-16 h-16 -rotate-90">
            <circle cx="32" cy="32" r="26" fill="none" stroke="#f3f4f6" strokeWidth="7" />
            <circle cx="32" cy="32" r="26" fill="none" stroke={tone} strokeWidth="7" strokeLinecap="round"
              strokeDasharray={`${(ev.score / 100) * circ} ${circ}`} className="transition-all duration-700" />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[15px] font-black tabular-nums" style={{ color: tone }}>{ev.score}</span>
        </div>
        <div className="min-w-0">
          <h2 className="text-[13.5px] font-semibold tracking-tight">{isEs ? 'Salud de publicación · multi-evidencia' : 'Publishing health · multi-evidence'}</h2>
          <p className="text-[11px] text-gray-400 max-w-lg leading-snug">{isEs ? ev.principle_es : ev.principle_en}</p>
        </div>
        <button onClick={confirmSighting} disabled={saving} data-testid="social-evidence-sighting"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-gray-200 hover:border-[#F77F11] px-3 py-1.5 text-[10.5px] font-bold text-gray-600 transition-colors disabled:opacity-50">
          👁 {saving ? '…' : (isEs ? 'Confirmo un post visto en LinkedIn' : 'I confirm a post seen on LinkedIn')}
        </button>
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {ev.evidences.map((e, i) => {
          const st = EV_STATUS[e.status] || EV_STATUS.none;
          return (
            <div key={e.key} className="rounded-xl border border-gray-100 bg-gray-50/50 p-3" data-testid={`social-evidence-${e.key}`}>
              <p className="flex items-center gap-1.5 text-[11px] font-bold text-gray-700">
                <span className="w-4 h-4 rounded bg-gray-900 text-white text-[9px] font-black flex items-center justify-center">{String.fromCharCode(65 + i)}</span>
                {e.label}
                {e.human && <span className="text-[8.5px] rounded-full bg-orange-100 text-[#d8651a] font-bold px-1.5 py-px uppercase">{isEs ? 'humana' : 'human'}</span>}
              </p>
              <p className={`mt-1 inline-block rounded-full border px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wide ${st.cls}`}>{isEs ? st.es : st.en}</p>
              <p className="mt-1 text-[10.5px] text-gray-500 leading-snug break-words">{isEs ? e.detail_es : e.detail_en}</p>
            </div>
          );
        })}
      </div>
      {ev.discrepancies?.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="social-evidence-discrepancies">
          {ev.discrepancies.map((x) => (
            <div key={x.rule} className="rounded-xl border border-amber-200 bg-amber-50/70 p-3">
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-600">⚖ {isEs ? 'Discrepancia en reconciliación' : 'Discrepancy under reconciliation'} · {x.between.join(' ↔ ')}</p>
              <p className="mt-0.5 text-[11.5px] text-gray-700 leading-snug">{isEs ? x.hypothesis_es : x.hypothesis_en}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function SocialIntelligencePage() {
  const { isEs } = useOs2();
  const [d, setD] = useState(null);
  const [checking, setChecking] = useState(false);
  const load = useCallback(() => {
    authFetch('/admin/social/center').then((r) => (r.ok ? r.json() : Promise.reject(r.status))).then(setD).catch(() => setD({ error: true }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const checkNow = async () => {
    setChecking(true);
    try { await authFetch('/admin/social/check', { method: 'POST' }); load(); } finally { setChecking(false); }
  };

  if (!d) return <LoadingState />;
  if (d.error) return <Card className="p-6 text-[12.5px] text-gray-500">Social Intelligence unavailable.</Card>;

  return (
    <div data-testid="social-intelligence-center">
      <PageHead
        title="Social Intelligence"
        question={isEs ? '¿Están sanos mis canales y qué generan? — una sola verdad sobre todos los proveedores.' : 'Are my channels healthy and what do they generate? — one truth above every provider.'}
      />
      <div className="-mt-2 mb-4 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-[10.5px] font-bold text-[#d8651a]" data-testid="social-autonomy">
          <ShieldCheck className="w-3 h-3" /> {isEs ? d.autonomy.label_es : d.autonomy.label_en}
        </span>
        <button onClick={checkNow} disabled={checking} data-testid="social-check-now"
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 hover:border-[#F77F11] px-3 py-1 text-[10.5px] font-semibold text-gray-600 transition-colors disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${checking ? 'animate-spin' : ''}`} /> {isEs ? 'Verificar ahora' : 'Check now'}
        </button>
        {d.last_sync && <span className="text-[10.5px] text-gray-400">{isEs ? 'Última sincronización' : 'Last sync'}: {timeAgo(d.last_sync, isEs)}</span>}
      </div>

      <Fade>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
          <Kpi testid="social-kpi-score" label="Social Health Score" value={`${d.health_score}`} accent={d.health_score >= 70 ? 'text-emerald-600' : d.health_score >= 40 ? 'text-[#d8651a]' : 'text-red-500'} />
          <Kpi testid="social-kpi-operational" label={isEs ? 'Canales operativos' : 'Operational channels'} value={`${d.operational}/${d.operational + d.degraded}`} />
          <Kpi testid="social-kpi-providers" label={isEs ? 'Proveedores activos' : 'Active providers'} value={d.providers.length} />
          <Kpi testid="social-kpi-distributed" label={isEs ? 'Contenido distribuido 7d' : 'Content distributed 7d'} value={d.content_distributed_7d} />
          <Kpi testid="social-kpi-traffic" label={isEs ? 'Tráfico social 28d' : 'Social traffic 28d'} value={d.traffic_28d} />
          <Kpi testid="social-kpi-conversions" label={isEs ? 'Conversiones 28d' : 'Conversions 28d'} value={d.conversions_28d} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-6" data-testid="social-channels-grid">
          {d.channels.map((c) => <ChannelCard key={`${c.provider}:${c.channel}`} c={c} isEs={isEs} />)}
        </div>

        <Card className="p-4" data-testid="social-events-feed">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
            <Radio className="w-3.5 h-3.5 text-[#F77F11]" /> {isEs ? 'Event Bus social · últimos 7 días' : 'Social Event Bus · last 7 days'}
          </p>
          {d.events.length ? d.events.slice(0, 15).map((e, i) => (
            <div key={e.id || i} className="flex items-start gap-2 py-1.5 border-b border-gray-50 last:border-0">
              <Zap className="w-3 h-3 mt-0.5 shrink-0 text-[#F77F11]" />
              <div className="min-w-0 flex-1">
                <p className="text-[11.5px] font-semibold text-gray-800">{e.type.replace(/^SOCIAL_/, '').replace(/_/g, ' ')}
                  {e.payload?.channel && <span className="ml-1 text-gray-400 font-normal">· {e.payload.channel}</span>}
                  {e.payload?.provider && e.payload.provider !== 'all' && <span className="text-gray-400 font-normal"> ({e.payload.provider})</span>}
                </p>
                {(e.payload?.summary || e.payload?.recommendation || e.payload?.reason) && (
                  <p className="text-[10.5px] text-gray-500 leading-snug">{e.payload.summary || e.payload.recommendation || e.payload.reason}</p>
                )}
              </div>
              <span className="shrink-0 text-[10px] text-gray-300">{timeAgo(e.created_at, isEs)}</span>
            </div>
          )) : <p className="text-[12px] text-gray-400 py-2">{isEs ? 'Sin eventos sociales aún — el monitor corre cada 30 min.' : 'No social events yet — the monitor runs every 30 min.'}</p>}
        </Card>

        <EvidencePanel isEs={isEs} />

        <p className="mt-4 text-[10.5px] text-gray-300 flex items-center gap-1.5">
          <Share2 className="w-3 h-3" />
          {isEs
            ? 'Cada estado se basa en evidencia etiquetada (API, agente, analytics, RSS, manual) — nunca un "conectado" genérico. UTM automático activo en RSS social y newsletter.'
            : 'Every status is evidence-labeled (API, agent, analytics, RSS, manual) — never a generic "connected". Auto-UTM active on social RSS and newsletter.'}
        </p>
      </Fade>
    </div>
  );
}
